/// <amd-module name="Wit/BacklogsHub/BacklogSettings/Components/AutomationSettingsView/AutomationTab" />
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { Checkbox } from "VSSUI/Checkbox";
import { Header, TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import {
    IObservableAutomationRulesStatesViewData,
    IOriginalAutomationRulesSettingData
} from "Wit/BacklogsHub/BacklogSettings/Components/AutomationSettingsView/AutomationSettingsInterfaces";
import * as Resources from "Wit/BacklogsHub/Resources";
import { AutomationRuleNames } from "Wit/Common/AutomationRulesService/Contracts/Contracts";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import * as React from "react";

export function getAutomationTab(
    originalRulesData: IOriginalAutomationRulesSettingData,
    rulesData: IObservableAutomationRulesStatesViewData,
    disabled: boolean,
    groupId?: string,
    onSaveSettings?: () => Promise<void>
): ISettingsView {
    const isDirty = new ObservableValue(false);
    const isValid = new ObservableValue(true);
    const setIsDirty = (): void => {
        const isDirtyNew =
            originalRulesData.isActiveEnabled !== rulesData.isActiveEnabled.value ||
            originalRulesData.isCompletedEnabled !== rulesData.isCompletedEnabled.value ||
            originalRulesData.isResolvedEnabled !== rulesData.isResolvedEnabled.value;

        if (isDirty.value !== isDirtyNew) {
            isDirty.value = isDirtyNew;
        }
    };

    const setCheckboxState = (checkboxObservableValue: IObservableValue<boolean>, checkboxState: boolean): void => {
        checkboxObservableValue.value = checkboxState;
    };

    return {
        groupId,
        isDirty,
        isValid,
        name: Resources.AutomationRulesTabTitle,
        onSaveSettings: onSaveSettings,
        renderContent: () => <AutomationTab rulesData={rulesData} disabled={disabled} setIsDirty={setIsDirty} setCheckboxState={setCheckboxState} />
    };
}

export function getAutomationRulesStateData(rulesViewSettings: IObservableAutomationRulesStatesViewData): { [key: string]: boolean } {
    const automationRulesState: { [key: string]: boolean } = {};
    automationRulesState[AutomationRuleNames.ActivateParentWhenAnyChildActivated] = rulesViewSettings.isActiveEnabled.value;
    automationRulesState[AutomationRuleNames.CompleteParentWhenAllChildrenCompleted] = rulesViewSettings.isCompletedEnabled.value;
    automationRulesState[AutomationRuleNames.ResolveParentWhenAllChildrenResolved] = rulesViewSettings.isResolvedEnabled.value;
    return automationRulesState;
}

export const getAutomationRulesSettings = (viewSettings: { [key: string]: boolean } | undefined): IOriginalAutomationRulesSettingData =>
    !viewSettings || Object.keys(viewSettings).length === 0
        ? {
              isActiveEnabled: false,
              isCompletedEnabled: false,
              isResolvedEnabled: false
          }
        : {
              isActiveEnabled: viewSettings[AutomationRuleNames.ActivateParentWhenAnyChildActivated],
              isCompletedEnabled: viewSettings[AutomationRuleNames.CompleteParentWhenAllChildrenCompleted],
              isResolvedEnabled: viewSettings[AutomationRuleNames.ResolveParentWhenAllChildrenResolved]
          };

interface IAutomationSettingsTabProps {
    rulesData: IObservableAutomationRulesStatesViewData;
    disabled: boolean;
    setIsDirty: () => void;
    setCheckboxState: (checkboxObservableValue: IObservableValue<boolean>, checkboxState: boolean) => void;
}

const AutomationTab: React.FC<IAutomationSettingsTabProps> = ({ rulesData, disabled, setIsDirty, setCheckboxState }) => {
    return (
        <div className="flex-column automation-settings-view rhythm-vertical-24">
            <Header
                className="padding-vertical-0"
                description={Resources.AutomationRulesDescription}
                title={Resources.AutomationRulesPanelTitle}
                titleSize={TitleSize.Small}
            >
                <Link target="_blank" rel="noopener noreferrer" href={Resources.AutomationRulesLearnMoreLink}>
                    {Resources.AutomationRulesLearnMoreText}
                </Link>
            </Header>
            <Observer isCompletedDisabled={rulesData.isResolvedEnabled} isResolvedDisabled={rulesData.isCompletedEnabled}>
                {({ isCompletedDisabled, isResolvedDisabled }) => {
                    return (
                        <div className="flex-column rhythm-vertical-24 padding-horizontal-20">
                            <div className="flex-column rhythm-horizontal-8">
                                <span className="font-weight-semibold">{Resources.AutomationRulesActiveLabel}</span>
                                <Checkbox
                                    checked={rulesData.isActiveEnabled}
                                    disabled={disabled}
                                    label={Resources.AutomationRulesActiveDescription}
                                    onChange={(_, checked) => {
                                        setCheckboxState(rulesData.isActiveEnabled, checked);
                                        setIsDirty();
                                    }}
                                />
                            </div>
                            <div className="flex-column rhythm-horizontal-8">
                                <span className="font-weight-semibold">{Resources.AutomationRulesCompletedOrResolvedLabel}</span>
                                <Checkbox
                                    checked={rulesData.isResolvedEnabled}
                                    disabled={disabled || isResolvedDisabled}
                                    label={Resources.AutomationRulesResolvedDescription}
                                    onChange={(_, checked) => {
                                        setCheckboxState(rulesData.isResolvedEnabled, checked);
                                        setIsDirty();
                                    }}
                                />
                                <Checkbox
                                    checked={rulesData.isCompletedEnabled}
                                    disabled={disabled || isCompletedDisabled}
                                    label={Resources.AutomationRulesCompletedDescription}
                                    onChange={(_, checked) => {
                                        setCheckboxState(rulesData.isCompletedEnabled, checked);
                                        setIsDirty();
                                    }}
                                />
                            </div>
                        </div>
                    );
                }}
            </Observer>
        </div>
    );
};
